import { IStargateClient } from '@atlassiansox/asc-stargate';
import { Environment, useStargateClient } from '@atlassiansox/bxpkit-core';
import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { v4 as generateUuid } from 'uuid';
import {
  BITBUCKET_ANALYTICS_EVENTS,
  logBitbucketAnalytics
} from './bitbucket-analytics';

export type PollForOrgCreationSuccessArgs = {
  readonly progressUri: string;
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
};

export type CreateAccountOrgArgs = {
  orgDisplayName: string;
  stargateClient: IStargateClient;
  createAnalyticsEvent: CreateUIAnalyticsEvent;
};

export type CheckBitbucketWorkspaceSlugAvailabilityArgs = {
  readonly workspaceSlug: string;
  readonly stargateClient: IStargateClient;
};

export const handleOrgCreate = async ({
  orgDisplayName,
  createAnalyticsEvent,
  stargateClient = useStargateClient()
}: CreateAccountOrgArgs): Promise<string | null> => {
  const orgCreateResponse = await stargateClient
    .cofsCreateOrg({ orgDisplayName })
    .then(({ orgId, progressUri }) => {
      const orgStatusUri = progressUri.split('/organization/')[1];
      // convert the polling call to originate from wac
      const pollUrl = `${Environment.getWacIntegrationUrl()}/gateway/api/cofs/${orgStatusUri}`;
      return { orgId, progressUri: pollUrl };
    })
    .catch((error) => {
      logBitbucketAnalytics({
        createAnalyticsEvent,
        analytics: {
          event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_CREATE} provisioned`,
          eventType: 'operational',
          action: 'errored',
          attributes: {
            errorCode: error?.response?.status,
            errorMessage: error?.message
          }
        }
      });
      return null;
    });

  if (!orgCreateResponse) {
    return null;
  }

  const { orgId, progressUri } = orgCreateResponse;

  const provisioningSuccess = await handlePollOrgCreation({
    progressUri,
    createAnalyticsEvent
  }).catch((error) => {
    logBitbucketAnalytics({
      createAnalyticsEvent,
      analytics: {
        event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_CREATE_STATUS} polled`,
        eventType: 'operational',
        action: 'errored',
        attributes: {
          errorCode: error?.response?.status,
          errorMessage: error?.message
        }
      }
    });
    return false;
  });

  logBitbucketAnalytics({
    createAnalyticsEvent,
    analytics: {
      event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_CREATE} provisioned`,
      eventType: 'operational',
      action: 'succeeded'
    }
  });

  return provisioningSuccess ? orgId : null;
};

export async function handlePollOrgCreation({
  progressUri,
  createAnalyticsEvent
}: PollForOrgCreationSuccessArgs) {
  let secondsPassed = 0;

  return new Promise((resolve, reject) => {
    const poll = setInterval(async () => {
      secondsPassed++;

      if (secondsPassed === 120) {
        logBitbucketAnalytics({
          createAnalyticsEvent,
          analytics: {
            event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_CREATE_STATUS} polled`,
            eventType: 'operational',
            action: 'errored',
            attributes: {
              errorMessage: 'Error timeout exceeded'
            }
          }
        });
        clearInterval(poll);
        reject('Error timeout exceeded');
      }

      const resp = await fetch(`${progressUri}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
        .then((res) => res.json())
        .catch((error) => {
          logBitbucketAnalytics({
            createAnalyticsEvent,
            analytics: {
              event: `${BITBUCKET_ANALYTICS_EVENTS.ORG_CREATE_STATUS} polled`,
              eventType: 'operational',
              action: 'errored',
              attributes: {
                errorCode: error?.response?.status,
                errorMessage: error?.message
              }
            }
          });
          return { completed: false };
        });

      if (resp.completed) {
        clearInterval(poll);

        if (resp.successful) {
          resolve(true);
        } else {
          resolve(false);
        }
      }
    }, 1000);
  });
}

export async function checkBitbucketWorkspaceSlugAvailability({
  workspaceSlug,
  stargateClient = useStargateClient()
}: CheckBitbucketWorkspaceSlugAvailabilityArgs): Promise<string> {
  return await stargateClient
    .bitbucketValidateWorkspaceSlug({
      workspaceSlug
    })
    .then(({ new_ari }) => new_ari);
}

export function generateBitbucketWorkspaceId(): string {
  return `ari:cloud:bitbucket::workspace/${generateUuid()}`;
}
